import React from 'react';
import {
  CheckCircle,
  Shield,
  Award,
  Calendar,
  User,
  Building as BuildingIcon,
  MapPin,
  Truck
} from 'lucide-react';

/* ============================================================
   Types — loosened so the component accepts partial objects
   ============================================================ */
interface Product {
  name?: string;
  brand?: string;
  batch?: string;
  warranty?: string;
  category?: string;
  image?: string;
  sku?: string;
  productId?: string;
}

interface Consumer {
  name?: string;
  email?: string;
  id?: string;
}

interface BusinessDetails {
  id?: string;
  businessId?: string;
  companyName?: string;
  tradingName?: string;
  registrationNumber?: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  contactPerson?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    position?: string;
  };
  verification?: {
    isVerified?: boolean;
    verifiedAt?: string;
  };
  establishedYear?: number;
  logoUrl?: string;
}

interface SupplyChainData {
  supplyChainId?: string;
  stages: Array<{
    id?: number | string;
    name?: string;
    location?: string;
    timestamp?: string;
    status?: string;
    vendor?: string;
    details?: string;
  }>;
  currentStage?: string;
  totalStages?: number;
}

interface StoreData {
  id?: string;
  storeId: string;
  storeName: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  manager?: string;
  phone?: string;
}

interface BatchData {
  id?: string;
  batchId: string;
  batchNumber?: string;
  manufacturing: {
    date?: string;
    location?: string;
    facility?: string;
  };
  expiryDate?: string;
  status?: string;
  isExpired?: boolean;
  qualityChecks?: Array<{
    type?: string;
    result?: string;
    date?: string;
  }>;
}

interface ProductDetails {
  id?: string;
  productId?: string;
  name?: string;
  brand?: string;
  category?: string;
  sku?: string;
  description?: string;
  specifications?: Record<string, string>;
  warranty?: string;
  image?: string;
}

interface DigitalCertificateProps {
  product: Product;
  tagId: string;
  consumer: Consumer;
  scanTime: string;
  // Only businessDetails is used for business info!
  businessDetails?: BusinessDetails | null;
  supplyChainData?: SupplyChainData | null;
  storeData?: StoreData | null;
  batchData?: BatchData | null;
  productDetails?: ProductDetails | null;
}


/* ============================================================
   Component
   ============================================================ */
export default function DigitalCertificate({
  product,
  tagId,
  consumer,
  scanTime,
  businessDetails,
  supplyChainData,
  storeData,
  batchData,
  productDetails
}: DigitalCertificateProps) {
  const certificateId = `CERT-${tagId}-${Date.now().toString().slice(-6)}`;
  const currentDate = new Date().toISOString();

  const safeStages = supplyChainData?.stages ?? [];

  return (
    <div className="max-w-6xl mx-auto bg-white shadow-2xl border-2 border-blue-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={businessDetails?.logoUrl || "/logo.png"}
              alt="Business Logo"
              className="h-12 w-12 bg-white rounded-lg p-2"
              onError={(e) => {
                e.currentTarget.src =
                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzNCODJGNiIvPgo8cGF0aCBkPSJNMTIgMTJIMjhWMTZIMTJWMTJaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTIgMThIMjhWMjJIMTJWMThaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTIgMjRIMjRWMjhIMTJWMjRaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
              }}
            />
            <div>
              <h1 className="text-3xl font-bold">JepTags Digital Certificate</h1>
              <p className="text-blue-100">Product Authenticity Verification</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 text-green-300 mb-2">
              <CheckCircle className="w-6 h-6" />
              <span className="font-semibold">VERIFIED AUTHENTIC</span>
            </div>
            <p className="text-sm text-blue-200">Certificate ID: {certificateId}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product Information */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Product Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Product Name</label>
                    <p className="text-lg font-semibold text-gray-900">{product.name || productDetails?.name || 'Unknown Product'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Brand</label>
                    <p className="text-lg font-semibold text-gray-900">{product.brand || productDetails?.brand || 'Unknown Brand'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Category</label>
                    <p className="text-lg font-semibold text-gray-900">{product.category || productDetails?.category || 'General'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Batch Number</label>
                    <p className="text-lg font-mono text-gray-900">{batchData?.batchNumber || product.batch || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Warranty</label>
                    <p className="text-lg font-semibold text-gray-900">{product.warranty || productDetails?.warranty || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Tag ID</label>
                    <p className="text-lg font-mono text-gray-900">{tagId}</p>
                  </div>
                  {product.sku || productDetails?.sku ? (
                    <div>
                      <label className="text-sm font-medium text-gray-500">SKU</label>
                      <p className="text-lg font-mono text-gray-900">{product.sku || productDetails?.sku}</p>
                    </div>
                  ) : null}
                 // Replace the specifications section with this TypeScript-safe version:

// Fixed specifications section for DigitalCertificate.tsx
// Replace the problematic specifications section around line 219-242

{productDetails?.specifications && Object.keys(productDetails.specifications).length > 0 && (
  <div>
    <label className="text-sm font-medium text-gray-500">Specifications</label>
    <div className="mt-1 text-sm text-gray-700">
      {Object.entries(productDetails.specifications).map(([key, value]) => {
        // Type-safe value processing
        const processValue = (val: unknown): string => {
          if (val === null || val === undefined) return '';
          
          if (typeof val === 'object' && val !== null) {
            // Handle structured specification objects
            const objVal = val as Record<string, unknown>;
            if ('value' in objVal && 'unit' in objVal) {
              return `${objVal.value || ''} ${objVal.unit || ''}`.trim();
            }
            // Fallback for other objects
            try {
              return JSON.stringify(val);
            } catch {
              return String(val);
            }
          }
          
          return String(val);
        };

        const displayValue = processValue(value);
        
        return (
          <div key={key} className="flex justify-between py-1">
            <span className="font-medium capitalize">
              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
            </span>
            <span className="text-right max-w-xs truncate" title={displayValue}>
              {displayValue || 'N/A'}
            </span>
          </div>
        );
      })}
    </div>
  </div>
)}
                </div>
              </div>
            </div>

            {/* Enhanced Business Information */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <BuildingIcon className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Business Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <img
                    src={businessDetails?.logoUrl || '/biz-placeholder.png'}
                    alt="Business Logo"
                    className="w-16 h-16 object-contain bg-white rounded-lg p-2 border"
                    onError={(e) => {
                      e.currentTarget.src =
                        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iOCIgZmlsbD0iI0Y5FUFGQUY5Ii8+CjxwYXRoIGQ9Ik0xNiAxNkg0OFYyMEgxNlYxNloiIGZpbGw9IiM2Qjc1ODUiLz4KPC9zdmc+';
                    }}
                  />
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {businessDetails?.companyName || 'Unknown Company'}
                    </p>
                    {businessDetails?.tradingName && (
                      <p className="text-sm text-gray-600">
                        <strong>Trading Name:</strong> {businessDetails.tradingName}
                      </p>
                    )}
                    {businessDetails?.registrationNumber && (
                      <p className="text-sm text-gray-600">
                        <strong>Registration #:</strong> {businessDetails.registrationNumber}
                      </p>
                    )}
                    {businessDetails?.industry && (
                      <p className="text-sm text-gray-600">{businessDetails.industry}</p>
                    )}
                    {businessDetails?.verification?.isVerified && (
                      <div className="flex items-center gap-1 mt-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600 font-medium">Verified Business</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {businessDetails?.website && businessDetails.website !== "" && (
                    <div className="flex items-center gap-2">
                      <a
                        href={businessDetails.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {businessDetails.website}
                      </a>
                    </div>
                  )}
                  {businessDetails?.email && (
                    <div className="text-gray-600">📧 {businessDetails.email}</div>
                  )}
                  {businessDetails?.phone && (
                    <div className="text-gray-600">📞 {businessDetails.phone}</div>
                  )}
                  {businessDetails?.address && (
                    <div className="text-gray-600">
                      <MapPin className="w-4 h-4 text-gray-400 inline-block mr-1" />
                      {businessDetails.address.street}, {businessDetails.address.city}, {businessDetails.address.state} {businessDetails.address.postalCode}, {businessDetails.address.country}
                    </div>
                  )}
                  {businessDetails?.contactPerson && (
                    <div className="text-gray-600">
                      <strong>Contact:</strong> {businessDetails.contactPerson.firstName} {businessDetails.contactPerson.lastName}, {businessDetails.contactPerson.position}<br />
                      <span>Email: {businessDetails.contactPerson.email}</span><br />
                      <span>Phone: {businessDetails.contactPerson.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Supply Chain Information */}
            {supplyChainData && (
              <div className="bg-yellow-50 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Truck className="w-6 h-6 text-yellow-600" />
                  <h2 className="text-xl font-bold text-gray-900">Supply Chain Tracking</h2>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    <strong>Supply Chain ID:</strong>{' '}
                    <span className="font-mono bg-yellow-100 px-2 py-1 rounded">
                      {supplyChainData.supplyChainId || 'N/A'}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Current Stage:</strong> {supplyChainData.currentStage || '-'} (
                    {safeStages.filter(s => (s.status || '').toLowerCase() === 'completed').length} of{' '}
                    {supplyChainData.totalStages ?? safeStages.length} completed)
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                    {safeStages.slice(0, 6).map((stage, index) => (
                      <div
                        key={`${stage.id ?? index}`}
                        className={`p-2 rounded border ${
                          (stage.status || '').toLowerCase() === 'completed'
                            ? 'bg-green-50 border-green-200'
                            : (stage.status || '').toLowerCase() === 'current'
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="font-medium text-gray-900">{stage.name || '-'}</div>
                        <div className="text-gray-600">{stage.location || '-'}</div>
                        <div className="text-gray-500">
                          {stage.timestamp ? new Date(stage.timestamp).toLocaleDateString() : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {safeStages.length > 6 && (
                    <p className="text-xs text-gray-500 text-center mt-2">
                      +{safeStages.length - 6} more stages in complete tracking
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Store & Batch Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {storeData && (
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold text-green-800">Store Information</h3>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div><strong>Store:</strong> {storeData.storeName}</div>
                    <div><strong>Location:</strong> {[storeData.address.city, storeData.address.state].filter(Boolean).join(', ')}</div>
                    {storeData.manager && <div><strong>Manager:</strong> {storeData.manager}</div>}
                  </div>
                </div>
              )}

              {batchData && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-800">Manufacturing Details</h3>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div><strong>Batch:</strong> {batchData.batchNumber || batchData.batchId}</div>
                    <div><strong>Manufactured:</strong> {batchData.manufacturing?.date ? new Date(batchData.manufacturing.date).toLocaleDateString() : '-'}</div>
                    <div><strong>Location:</strong> {batchData.manufacturing?.location || '-'}</div>
                    <div><strong>Expires:</strong> {batchData.expiryDate ? new Date(batchData.expiryDate).toLocaleDateString() : '-'}</div>
                    {batchData.isExpired && (
                      <div className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
                        ⚠️ EXPIRED
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Consumer Verification */}
            <div className="bg-green-50 rounded-lg p-6 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <User className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">Consumer Verification</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Verified by</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {consumer?.name || consumer?.email || 'Anonymous User'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Verification Date</label>
                  <p className="text-lg font-semibold text-gray-900">{scanTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Image & Verification Status */}
          <div className="lg:col-span-1">
            <div className="bg-white border rounded-lg p-6 mb-6">
              <img
                src={product.image || productDetails?.image || '/placeholder.png'}
                alt="Product"
                className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 mb-4"
                onError={(e) => {
                  e.currentTarget.src =
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUI5Qjk5Ii8+Cjwvc3ZnPgo=';
                }}
              />

              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <span className="text-2xl font-bold text-green-600">AUTHENTIC</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Verified by JepTags Anti-Counterfeiting Technology
                </p>

                <div className="bg-green-100 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 justify-center text-green-800">
                    <Award className="w-5 h-5" />
                    <span className="font-semibold">Certificate Valid</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    This product has passed all authenticity checks
                  </p>
                </div>
              </div>
            </div>

            {/* Certificate Details */}
            <div className="bg-gray-50 border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Certificate Details</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="text-gray-500">Certificate ID</label>
                  <p className="font-mono text-gray-900">{certificateId}</p>
                </div>
                <div>
                  <label className="text-gray-500">Generated</label>
                  <p className="text-gray-900">{new Date().toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-gray-500">Technology</label>
                  <p className="text-gray-900">JepTags Blockchain Verification</p>
                </div>
                <div>
                  <label className="text-gray-500">Status</label>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    <CheckCircle className="w-3 h-3" />
                    Valid & Verified
                  </span>
                </div>
                {supplyChainData && (
                  <div>
                    <label className="text-gray-500">Supply Chain</label>
                    <p className="text-gray-900">Full traceability available</p>
                  </div>
                )}
                {businessDetails?.verification?.isVerified && (
                  <div>
                    <label className="text-gray-500">Business Status</label>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      <Shield className="w-3 h-3" />
                      Verified Business
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-100 border-t px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            <p><strong>Certificate generated:</strong> {currentDate}</p>
            <p>Powered by JepTags anti-counterfeiting technology.</p>
            {supplyChainData && (
              <p className="mt-1">
                Complete supply chain tracking:{' '}
                {supplyChainData.totalStages ?? safeStages.length} verified stages
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Shield className="w-4 h-4" />
            <span>Blockchain secured • Tamper-proof • Globally verified</span>
          </div>
        </div>
      </div>
    </div>
  );
}